import { IconButton, Link, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Snackbar, Box, Switch, type AlertColor } from "@mui/material";
import Api from "api";
import { ReactNode, useEffect, useLayoutEffect, useState, type ChangeEvent, type MouseEvent } from "react";
import { TransitionSlideLeft } from "components/Transition";
import AlertStyled from "components/AlertStyled";
import type { ResGetDomains } from "api/Domains";
import getSearchParamsValue from "utils/getSearchParamsValue";
import { useSearchParams } from "react-router-dom";
import { TablePaginationActions } from "./Emails";
import DeleteDomain from "./DeleteDomain";
import CreateDomain from "components/createDomain";

const COLS: string[] = [
	"No",
	"Domain",
	"maxUsers",
	"registerCountUsers",
	"Status",
	"Actions"
];

function Domains()
{

	const [ searchParams, setSearchParams ] = useSearchParams();
	const [ domains, setDomains ] = useState<ResGetDomains[]>([]);
	const [ isFetching, setIsFetch ] = useState<boolean>(false);
	const [ snackbarContent, setSnackbarContent ] = useState<null | { severity: AlertColor; content: ReactNode; }>(null);
	const [ rowsPerPage, setRowsPerPage ] = useState<number>(getSearchParamsValue(searchParams, "rowsPerPage", "number") ?? 10);
	const [ page, setPage ] = useState<number>(() =>
	{
		const value = getSearchParamsValue(searchParams, "page", "number");

		return value === null ? 0 : value - 1;
	});

	useEffect(() =>
	{
		Api.domains
			.getDomains()
			.then((data) =>
			{
				setDomains(data);
			})
			.catch((err) =>
			{
				console.error(err);
			})
			.finally(() => setIsFetch(false));
	}, []);

	useLayoutEffect(() =>
	{
		const updatedParams = new URLSearchParams(searchParams);

		updatedParams.set("page", String(page + 1));
		updatedParams.set("rowsPerPage", String(rowsPerPage));

		setSearchParams(updatedParams);
	}, [ page, rowsPerPage, searchParams, setSearchParams ]);

	function handleChangePage(_event: MouseEvent<HTMLButtonElement> | null, newPage: number)
	{
		setPage(newPage);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	function handleChangeRowsPerPage(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)
	{
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	}

	function handleSwitchChange(id: string, newValue: boolean)
	{

		Api.domains
			.changeActive(id, newValue)
			.then((data) =>
			{
				setSnackbarContent({ severity: "success", content: "Success update status domain" });
				setDomains((prevDomains) =>
				{
					return prevDomains.map((dom) =>
					{
						if (dom._id === data._id)
						{
							return { ...dom, isActive: data.isActive };
						}

						return dom;
					});
				});
			})
			.catch((err) =>
			{
				setSnackbarContent({ severity: "error", content: "An error occurred" });
				console.error(err);
			})
			.finally(() => setIsFetch(false));
	}

	return (
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
			<CreateDomain domains={domains} setDomains={setDomains} />
			<Table>
				<TableHead>
					<TableRow>
						{COLS.map((col) =>
						{
							return (
								<TableCell
									key={col}
									sx={{
										fontWeight: "bold",
										verticalAlign: "top",
										whiteSpace: "nowrap"
									}}
								>
									{col}
								</TableCell>
							);
						})}
					</TableRow>
				</TableHead>
				<TableBody>
					{isFetching === true &&
						(
							<TableRow>
								<TableCell colSpan={COLS.length} sx={{ fontSize: "18px", textAlign: "center" }}>
									Loading...
								</TableCell>
							</TableRow>
						)
					}
					{(rowsPerPage > 0
						? domains.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
						: domains
					).map((domain, i) => (
						<TableRow key={i}>
							<TableCell>{i + 1}</TableCell>
							<TableCell>
								<Link href={`/admin/users/?domain=${ domain.domain }`}>
									{domain.domain}
								</Link>
							</TableCell>
							<TableCell>{domain.maxUsers}</TableCell>
							<TableCell>{domain.registerCountUsers}</TableCell>
							<TableCell> Off
								<Switch
									checked={domain.isActive}
									onChange={() => handleSwitchChange(domain._id, !domain.isActive)}
									color="primary"
								/> On
							</TableCell>
							<TableCell>
								<DeleteDomain id={domain._id} setDomains={setDomains} />
							</TableCell>
						</TableRow>
					))}
				</TableBody>
				<TableFooter>
					<TableRow>
						<TablePagination
							rowsPerPageOptions={[ 10, 15, 25, 50, { label: "All", value: -1 } ]}
							colSpan={COLS.length}
							count={domains.length}
							rowsPerPage={rowsPerPage}
							page={page}
							SelectProps={{ inputProps: { "aria-label": "rows per page" }, variant: "standard" }}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							ActionsComponent={TablePaginationActions}
						/>
					</TableRow>
				</TableFooter>
			</Table>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				open={snackbarContent !== null}
				autoHideDuration={3000}
				onClose={() => setSnackbarContent(null)}
				TransitionComponent={TransitionSlideLeft}
			>
				<AlertStyled onClose={() => setSnackbarContent(null)} severity={snackbarContent?.severity}>
					{snackbarContent?.content}
				</AlertStyled>
			</Snackbar>
		</Box>
	);

}

export default Domains;
